import {
  Archive,
  Download,
  File,
  FileImage,
  FileText,
  LoaderCircle,
  Paperclip,
  Trash2,
  UploadCloud,
} from 'lucide-react'
import { type ChangeEvent, type DragEvent, useCallback, useEffect, useRef, useState } from 'react'

import { LIMITS } from '../../shared/constants'
import type { AttachmentRecord } from '../../shared/types'
import { ApiClientError, apiFetch, apiRequest } from '../api'
import { attachmentKind, formatAttachmentSize } from '../attachments'

const kindIcons = {
  archive: Archive,
  document: FileText,
  file: File,
  image: FileImage,
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof ApiClientError ? reason.message : fallback
}

export function AttachmentPanel({ documentId }: { documentId: string }) {
  const [attachments, setAttachments] = useState<AttachmentRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [confirming, setConfirming] = useState<AttachmentRecord | null>(null)
  const [dragging, setDragging] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setLoadFailed(false)
    setError('')
    try {
      setAttachments(await apiFetch<AttachmentRecord[]>(`/api/documents/${documentId}/attachments`))
    } catch (reason) {
      setLoadFailed(true)
      setError(errorMessage(reason, '附件加载失败，请重试'))
    } finally {
      setLoading(false)
    }
  }, [documentId])

  useEffect(() => { void load() }, [load])

  async function upload(file: File | undefined) {
    if (!file || uploading) return
    setError('')
    setNotice('')
    if (file.size <= 0) {
      setError('不能上传空文件')
      return
    }
    if (file.size > LIMITS.attachmentBytes) {
      setError('单个附件不能超过 10 MB')
      return
    }
    setUploading(true)
    const form = new FormData()
    form.set('file', file)
    try {
      const created = await apiFetch<AttachmentRecord>(`/api/documents/${documentId}/attachments`, {
        method: 'POST',
        body: form,
      })
      setAttachments((current) => [created, ...current])
      setNotice(`已上传 ${created.fileName}`)
    } catch (reason) {
      setError(errorMessage(reason, '附件上传失败，请重试'))
    } finally {
      setUploading(false)
      if (inputRef.current) inputRef.current.value = ''
    }
  }

  function choose(event: ChangeEvent<HTMLInputElement>) {
    void upload(event.target.files?.[0])
  }

  function drop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    setDragging(false)
    void upload(event.dataTransfer.files[0])
  }

  async function download(attachment: AttachmentRecord) {
    setDownloading(attachment.id)
    setError('')
    try {
      const response = await apiRequest(`/api/attachments/${attachment.id}/download`)
      const url = URL.createObjectURL(await response.blob())
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = attachment.fileName
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      URL.revokeObjectURL(url)
    } catch (reason) {
      setError(errorMessage(reason, '附件下载失败，请重试'))
    } finally {
      setDownloading(null)
    }
  }

  async function remove() {
    if (!confirming) return
    setDeleting(true)
    setError('')
    try {
      await apiFetch(`/api/attachments/${confirming.id}`, { method: 'DELETE' })
      setAttachments((current) => current.filter((item) => item.id !== confirming.id))
      setNotice(`已删除 ${confirming.fileName}`)
      setConfirming(null)
    } catch (reason) {
      setError(errorMessage(reason, '附件删除失败，请重试'))
    } finally {
      setDeleting(false)
    }
  }

  return (
    <aside className="side-panel attachment-panel" aria-label="附件面板">
      <div className="panel-heading">
        <div><p className="eyebrow">FILES</p><h2>附件</h2></div><span>{attachments.length}</span>
      </div>
      <label
        className={`attachment-dropzone ${dragging ? 'dragging' : ''} ${uploading ? 'busy' : ''}`}
        data-testid="attachment-dropzone"
        htmlFor="attachment-file"
        onDragEnter={(event) => { event.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDragOver={(event) => event.preventDefault()}
        onDrop={drop}
      >
        {uploading ? <LoaderCircle className="spin" size={24} /> : <UploadCloud size={24} />}
        <strong>{uploading ? '正在上传…' : '拖放文件到这里'}</strong>
        <span>或点击选择文件 · 单个不超过 10 MB</span>
        <input ref={inputRef} id="attachment-file" className="sr-only" type="file" aria-label="选择附件" disabled={uploading} onChange={choose} />
      </label>
      <div className="attachment-feedback" role="status" aria-live="polite">
        {notice || (uploading ? '正在上传附件' : '')}
      </div>
      {error ? <div className="attachment-error" role="alert"><p>{error}</p>{loadFailed ? <button type="button" onClick={() => void load()}>重新加载</button> : null}</div> : null}
      {loading ? <p className="muted">正在整理附件…</p> : null}
      {!loading && !loadFailed && !attachments.length ? <div className="panel-empty attachment-empty"><Paperclip size={25} /><p>还没有附件。把相关文件放在这份文档旁边吧。</p></div> : null}
      <div className="attachment-list">
        {attachments.map((attachment) => {
          const KindIcon = kindIcons[attachmentKind(attachment.contentType)]
          return (
            <article className="attachment-item" key={attachment.id}>
              <span className={`attachment-kind ${attachmentKind(attachment.contentType)}`}><KindIcon size={18} /></span>
              <div className="attachment-copy">
                <strong title={attachment.fileName}>{attachment.fileName}</strong>
                <small>{formatAttachmentSize(attachment.sizeBytes)} · {attachment.actor}</small>
                <time>{new Intl.DateTimeFormat('zh-CN', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(attachment.createdAt))}</time>
              </div>
              <div className="attachment-actions">
                <button type="button" disabled={downloading === attachment.id} onClick={() => void download(attachment)} aria-label={`下载“${attachment.fileName}”`} title="下载">
                  {downloading === attachment.id ? <LoaderCircle className="spin" size={16} /> : <Download size={16} />}
                </button>
                <button type="button" onClick={() => setConfirming(attachment)} aria-label={`删除“${attachment.fileName}”`} title="删除"><Trash2 size={16} /></button>
              </div>
            </article>
          )
        })}
      </div>
      {confirming ? <div className="modal-backdrop panel-modal"><section className="modal-card danger-dialog" role="dialog" aria-modal="true" aria-labelledby="attachment-delete-heading"><h2 id="attachment-delete-heading">删除附件？</h2><p>“{confirming.fileName}”将被永久删除，此操作无法撤销。</p><div className="modal-actions"><button className="ghost-button" type="button" onClick={() => setConfirming(null)} disabled={deleting}>取消</button><button className="danger-button" type="button" onClick={() => void remove()} disabled={deleting}>{deleting ? '删除中…' : '确认删除'}</button></div></section></div> : null}
    </aside>
  )
}
